import * as React from "react"
import { Row, Col } from "react-grid-system"
import { ThemeProvider, styled } from "styled-components"
import { DeepPartial } from "edge-proto/dist/edge/v1/metrics"
import { DeviceMetrics, useEdgeApi } from "../../edge/EdgeProvider"
import { AntennaOrientation, LocalOrientation } from "../../types"
import { BeamListEntry } from "../../edge/device_gen/motion"
import { signalQuality } from "../../utils/signal-strength"
import { formatOrbitalSlot } from "../../utils/formatters"
import useSignalStrengthHistory from "../../hooks/useSignalStrengthHistory"
import { HorizontalSpacer } from "../atoms/spacers"
import PageContainer from "../atoms/PageContainer"
import SatelliteSignalCard from "../organisms/overview/SatelliteSignalCard"
import RadarSignalCard from "../organisms/overview/RadarSignalCard"
import { signalTheme } from "../theme"

const SignalPageShell = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.greyDarker};
`

const currentOrientation = (metrics: DeviceMetrics): DeepPartial<AntennaOrientation> => ({
  polarization: { value: metrics.useCurrentOrientationPolarization(), orientation: undefined },
  azimuth: metrics.useCurrentOrientationAzimuth(),
  elevation: metrics.useCurrentOrientationElevation(),
})

const targetOrientation = (metrics: DeviceMetrics): DeepPartial<AntennaOrientation> => ({
  polarization: { value: metrics.useTargetOrientationPolarization(), orientation: undefined },
  azimuth: metrics.useTargetOrientationAzimuth(),
  elevation: metrics.useTargetOrientationElevation(),
})

const targetLocalOrientation = (metrics: DeviceMetrics): DeepPartial<LocalOrientation> => ({
  alpha: metrics.useTargetLocalOrientationAlpha(),
  beta: metrics.useTargetLocalOrientationBeta(),
  gamma: metrics.useTargetLocalOrientationGamma(),
})

const motorLocalOrientation = (metrics: DeviceMetrics): DeepPartial<LocalOrientation> => ({
  alpha: metrics.useMotorLocalOrientationAlpha(),
  beta: metrics.useMotorLocalOrientationBeta(),
  gamma: undefined,
})

const OTHER_SATELLITE_ANGLES = [-1, 10, 23]

const SignalPage: React.FunctionComponent<{
  beamSwitchModalOpen: boolean
  setBeamSwitchModalOpen: (open: boolean) => void
}> = ({ beamSwitchModalOpen, setBeamSwitchModalOpen }) => {
  const { metrics, config } = useEdgeApi()
  const cOverN = metrics.useModemSignalStrength()
  const powerDetSignal = metrics.usePowerDetectorStrength()
  const beams = metrics.useBeamList()
  const selectedBeam = beams?.find((beam: BeamListEntry) => beam.id === config.useBeamInfoId("")?.value)
  const signalStrengthHistory = useSignalStrengthHistory()

  return (
    <ThemeProvider theme={signalTheme}>
      <SignalPageShell>
        <PageContainer wide fullHeight>
          <HorizontalSpacer spacing={4} />
          <Row>
            <Col lg={12} xl={6}>
              <SatelliteSignalCard
                currentOrientation={currentOrientation(metrics)}
                targetOrientation={targetOrientation(metrics)}
                targetLocalOrientation={targetLocalOrientation(metrics)}
                motorLocalOrientation={motorLocalOrientation(metrics)}
                cOverN={cOverN}
                powerDetSignal={powerDetSignal}
                signalQuality={cOverN ? signalQuality(cOverN) : 0}
                beams={beams}
                longitudeSatellite={formatOrbitalSlot(metrics.useSatellitePositionLon())}
                rf={{ rx: metrics.useCarrierCarrier() }}
                bandwidth={{ rx: metrics.useCarrierBandwidth() }}
                lo={{ rx: metrics.useCarrierRxLo(), tx: metrics.useCarrierTxLo() }}
                beamSwitchModalOpen={beamSwitchModalOpen}
                setBeamSwitchModalOpen={setBeamSwitchModalOpen}
                showBeamSwitch={false}
              />
              <HorizontalSpacer spacing={2} />
            </Col>
            <Col lg={12} xl={6}>
              <RadarSignalCard
                motorLocalOrientation={motorLocalOrientation(metrics).alpha}
                targetOrientation={targetOrientation(metrics).azimuth}
                targetSatelliteName={selectedBeam?.name}
                currentOrientation={currentOrientation(metrics).azimuth}
                currentElevation={currentOrientation(metrics).elevation}
                otherSatelliteAngles={OTHER_SATELLITE_ANGLES}
                signalStrengthHistory={signalStrengthHistory}
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={4} />
        </PageContainer>
      </SignalPageShell>
    </ThemeProvider>
  )
}

export default SignalPage
